import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';
import '../users/userform.css';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../services/AxiosInstance";
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import DateInput from '../components/Forms/DateInput';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from "yup";
import Select from 'react-select';
import { createVehicle } from "../../services/VehicleService";
import { listStaffByCorporate } from "../../services/StaffService";

const AddVehicle = () => {
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);   
    const [branches, setBranches] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [filteredDepartments, setFilteredDepartments] = useState([]);
    const [subDepartments, setSubDepartments] = useState([]);
    const [filteredSubDepartments, setFilteredSubDepartments] = useState([]);
    const [staffList, setStaffList] = useState([]);

    const notifyTopRight = (data) => {
        toast.success(`✔️ ${data}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
        });
    };

    const notifyError = (data) => {
        toast.error(`❌ ${data}`, {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
        });
    };
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Helper function to safely navigate back, avoiding login page
    const navigateBackSafely = () => {
        // Simple approach: try navigate(-1) and if it goes to login, redirect to project list
        navigate(-1);

        // Set a timeout to check if we ended up on login page
        setTimeout(() => {
            if (window.location.pathname.includes('/login')) {
                const search = searchParams.get('search') || '';
                navigate(`/task/projectlist${search ? `?search=${encodeURIComponent(search)}` : ''}`);
            }
        }, 100);
    };
    const validationSchema = Yup.object().shape({
      registration_number: Yup.string()
        .trim()
        .required("Registration number is required")
        .matches(/^[A-Z0-9- ]+$/i, "Invalid registration number format")
        .max(50, "Registration number cannot exceed 50 characters")
        .test('no-only-special-chars', 'Registration number cannot contain only special characters.', (value) => {
          if (!value || !String(value).trim()) return true;
          return /[a-zA-Z0-9]/.test(String(value).trim());
        }),
      vehicle_type: Yup.string()
        .oneOf(['Two-wheeler', 'Four-wheeler', 'Van', 'Pickup', 'Bus', 'Others'], "Please select a valid vehicle type"),
      ownership_type: Yup.string()
        .oneOf(['Employee-owned', 'Company-owned'], "Ownership type must be Employee-owned or Company-owned"),
      usage_type: Yup.string()
        .oneOf(['Personal', 'Official', 'Both'], "Usage type must be Personal, Official, or Both"),
      status: Yup.string()
        .oneOf(['Requested', 'Active', 'Expired', 'Deactivated'], "Status must be Requested, Active, Expired, or Deactivated"),
    });

    const fetchBranches = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            const corporateId = userData?.corporateId || userData?.id;
            if (!corporateId) return;
            const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
            const data = res.data;
            setBranches(data?.data || data || []);
        } catch (error) {
            console.error('Error fetching branches:', error);
            notifyError('Failed to load branches');
        }
    };

    const fetchDepartments = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            const corporateId = userData?.corporateId || userData?.id;
            if (!corporateId) return;
            const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
            const data = res.data;
            const list = data?.data || data || [];
            setDepartments(list);
            setFilteredDepartments(list);
        } catch (error) {
            console.error('Error fetching departments:', error);
            notifyError('Failed to load departments');
        }
    };

    const fetchSubDepartments = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            const corporateId = userData?.corporateId || userData?.id;
            if (!corporateId) return;
            let res;
            try {
                res = await axiosInstance.get(`/settings/subDepartments?corporate_id=${corporateId}`);
            } catch (e1) {
                res = await axiosInstance.get(`/settings/subdepartments?corporate_id=${corporateId}`);
            }
            const data = res.data;
            const list = data?.data || data || [];
            setSubDepartments(list);
            setFilteredSubDepartments(list);
        } catch (error) {
            console.error('Error fetching sub-departments:', error);
            notifyError('Failed to load sub-departments');
        }
    };

    const fetchStaff = async () => {
        try {
            const userData = JSON.parse(localStorage.getItem('user'));
            const corporateId = userData?.corporateId || userData?.id;
            if (!corporateId) return;
            const res = await listStaffByCorporate(corporateId, { limit: 1000 });
            if (res.data.success) {
                setStaffList(res.data.data || []);
            }
        } catch (error) {
            console.error('Error fetching staff:', error);
        }
    };

    useEffect(() => {
        fetchBranches();
        fetchDepartments();
        fetchSubDepartments();
        fetchStaff();
    }, []);

    useEffect(() => {
        setFilteredDepartments(departments);
    }, [departments]);

    useEffect(() => {
        setFilteredSubDepartments(subDepartments);
    }, [subDepartments]);

 
    const handleBranchSelect = (selectedOption, setFieldValue) => {
        const branchId = selectedOption ? selectedOption.value : '';
        setFieldValue('branchId', branchId);
        setFieldValue('departmentId', '');
        setFieldValue('subDepartmentId', '');

        if (branchId) {
            const filtered = departments.filter((department) => {
                const deptBranchId = department?.branch_id ?? department?.branchId;
                return String(deptBranchId) === String(branchId);
            });
            setFilteredDepartments(filtered);

            const subFiltered = subDepartments.filter((subDepartment) => {
                const subBranchId = subDepartment?.branch_id ?? subDepartment?.branchId;
                return String(subBranchId) === String(branchId);
            });
            setFilteredSubDepartments(subFiltered);
        } else {
            setFilteredDepartments(departments);
            setFilteredSubDepartments(subDepartments);
        }
    };

    const handleDepartmentSelect = (selectedOption, setFieldValue, currentBranchId) => {
        const departmentId = selectedOption ? selectedOption.value : '';
        setFieldValue('departmentId', departmentId);
        setFieldValue('subDepartmentId', '');

        if (departmentId) {
            const filtered = subDepartments.filter((subDepartment) => {
                const subDeptId = subDepartment?.department_id ?? subDepartment?.departmentId;
                return String(subDeptId) === String(departmentId);
            });
            setFilteredSubDepartments(filtered);
        } else if (currentBranchId) {
            const filtered = subDepartments.filter((subDepartment) => {
                const subBranchId = subDepartment?.branch_id ?? subDepartment?.branchId;
                return String(subBranchId) === String(currentBranchId);
            });
            setFilteredSubDepartments(filtered);
        } else {
            setFilteredSubDepartments(subDepartments);
        }
    };

    const handleSubDepartmentSelect = (selectedOption, setFieldValue) => {
        const subDepartmentId = selectedOption ? selectedOption.value : '';
        setFieldValue('subDepartmentId', subDepartmentId);
    };

    const handleSubmit = async (values) => {     
        setLoading(true);
        setIsSubmitting(true);

        const userData = JSON.parse(localStorage.getItem("user"));

        const payload = {
            corporate_id: userData.corporateId,
            registration_number: values.registration_number,
            staff_id: values.staff_id || null,
            vehicle_type: values.vehicle_type || null,
            ownership_type: values.ownership_type || null,
            maker: values.maker || null,
            model: values.model || null,
            color: values.color || null,
            mileage: values.mileage ? parseFloat(values.mileage) : null,
            usage_type: values.usage_type || null,
            status: values.status || 'Requested',
            vehicle_purchase_value: values.vehicle_purchase_value ? parseFloat(values.vehicle_purchase_value) : null,
            branch_id: values.branchId || null,
            department_id: values.departmentId || null,
            sub_department_id: values.subDepartmentId || null,
            created_by: userData.id || null,
        };

        try {
            const response = await createVehicle(payload);

            if (!response) {
                throw new Error('Failed to submit the form');
            }

            console.log('Form submitted successfully:', response);
            notifyTopRight('✅ Vehicle created successfully!');

            setTimeout(() => {
                setIsSubmitting(false);
                navigate('/vehicle/vehicleList');
            }, 1500);

        } catch (error) {
            console.error('Submission error:', error);
            if (error.response?.status === 409) {
                notifyError('Registration number already exists');
            } else {
                notifyError(error.response?.data?.message || error.response?.data?.error || 'Failed to create Vehicle');
            }
            setIsSubmitting(false);
        } finally {
            setLoading(false);
        }
    };


    return (
        <div>
            <button
                className="back-button"
                onClick={() => {
                    if (window.history.length > 2) {
                        navigate(-1);
                    } else {
                        navigate('/vehicle/vehicleList');
                    }
                }}
            >
                <FiArrowLeft style={{ marginRight: '0.5rem' }} />
                Back
            </button>
            <div className="row" style={{ marginBottom: '0px !important' }}>
                <ToastContainer />
                <div className="col-xl-12 col-xxl-12">
                    <div className="card">
                        <div className="card-body">
                            <section>
                                <Formik
                                    initialValues={{
                                        registration_number: '',
                                        staff_id: '',
                                        vehicle_type: '',
                                        ownership_type: '',
                                        maker: '',
                                        model: '',
                                        color: '',
                                        mileage: '',
                                        usage_type: '',
                                        status: 'Requested',
                                        vehicle_purchase_value: '',
                                        branchId: '',
                                        departmentId: '',
                                        subDepartmentId: ''
                                    }}
                                    validationSchema={validationSchema}
                                    enableReinitialize={true}
                                    onSubmit={handleSubmit}
                                  >
                                    {({ values, setFieldValue, errors, isSubmitting, handleChange, touched }) => (
                                        <Form>
                                            <div className="row">
                                                <div className="col-lg-6 mb-2">
                                                    <div className="form-group mb-3">
                                                        <label className="text-label">Registration Number*</label>
                                                        <input
                                                            type="text"
                                                            name="registration_number"
                                                            className={`form-control ${errors.registration_number && touched.registration_number ? 'is-invalid' : ''}`}
                                                            placeholder="Enter registration number"
                                                            value={values.registration_number}
                                                            onChange={handleChange}
                                                        />
                                                        {errors.registration_number && touched.registration_number && <div className="text-danger">{errors.registration_number}</div>}
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <label className="text-label">Assigned Staff</label>
                                                    <Select
                                                        className="basic-single"
                                                        classNamePrefix="select"
                                                        options={staffList.map((staff) => ({
                                                            value: staff.id,
                                                            label: `${staff.employee_code || ''} - ${[staff.first_name, staff.middle_name, staff.last_name].filter(Boolean).join(' ') || 'N/A'}`,
                                                            data: staff,
                                                        }))}
                                                        value={
                                                            values.staff_id
                                                                ? staffList
                                                                    .map((staff) => ({
                                                                        value: staff.id,
                                                                        label: `${staff.employee_code || ''} - ${[staff.first_name, staff.middle_name, staff.last_name].filter(Boolean).join(' ') || 'N/A'}`,
                                                                        data: staff,
                                                                    }))
                                                                    .find((option) => String(option.value) === String(values.staff_id)) || null
                                                                : null
                                                        }
                                                        onChange={(selectedOption) => setFieldValue('staff_id', selectedOption ? selectedOption.value : '')}
                                                        isClearable
                                                        placeholder="Select Staff (Optional)"
                                                    />
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <label className="text-label">Vehicle Type</label>
                                                    <select
                                                        name="vehicle_type"
                                                        className="form-control"
                                                        value={values.vehicle_type}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Vehicle Type</option>
                                                        <option value="Two-wheeler">Two-wheeler</option>
                                                        <option value="Four-wheeler">Four-wheeler</option>
                                                        <option value="Van">Van</option>
                                                        <option value="Pickup">Pickup</option>
                                                        <option value="Bus">Bus</option>
                                                        <option value="Others">Others</option>
                                                    </select>
                                                    {errors.vehicle_type && touched.vehicle_type && <div className="text-danger">{errors.vehicle_type}</div>}
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <label className="text-label">Ownership Type</label>
                                                    <select
                                                        name="ownership_type"
                                                        className="form-control"
                                                        value={values.ownership_type}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Ownership Type</option>
                                                        <option value="Employee-owned">Employee-owned</option>
                                                        <option value="Company-owned">Company-owned</option>
                                                    </select>
                                                    {errors.ownership_type && touched.ownership_type && <div className="text-danger">{errors.ownership_type}</div>}
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <div className="form-group mb-3">
                                                        <label className="text-label">Maker</label>
                                                        <input
                                                            type="text"
                                                            name="maker"
                                                            className="form-control"
                                                            placeholder="Enter maker (e.g., Toyota)"
                                                            value={values.maker}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <div className="form-group mb-3">
                                                        <label className="text-label">Model</label>
                                                        <input
                                                            type="text"
                                                            name="model"
                                                            className="form-control"
                                                            placeholder="Enter model (e.g., Camry)"
                                                            value={values.model}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <div className="form-group mb-3">
                                                        <label className="text-label">Color</label>
                                                        <input
                                                            type="text"
                                                            name="color"
                                                            className="form-control"
                                                            placeholder="Enter color"
                                                            value={values.color}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <div className="form-group mb-3">
                                                        <label className="text-label">Mileage</label>
                                                        <input
                                                            type="number"
                                                            name="mileage"
                                                            className="form-control"
                                                            placeholder="Enter mileage"
                                                            step="0.01"
                                                            value={values.mileage}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <label className="text-label">Usage Type</label>
                                                    <select
                                                        name="usage_type"
                                                        className="form-control"
                                                        value={values.usage_type}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="">Select Usage Type</option>
                                                        <option value="Personal">Personal</option>
                                                        <option value="Official">Official</option>
                                                        <option value="Both">Both</option>
                                                    </select>
                                                    {errors.usage_type && touched.usage_type && <div className="text-danger">{errors.usage_type}</div>}
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <label className="text-label">Status*</label>
                                                    <select
                                                        name="status"
                                                        className={`form-control ${errors.status && touched.status ? 'is-invalid' : ''}`}
                                                        value={values.status}
                                                        onChange={handleChange}
                                                    >
                                                        <option value="Requested">Requested</option>
                                                        <option value="Active">Active</option>
                                                        <option value="Expired">Expired</option>
                                                        <option value="Deactivated">Deactivated</option>
                                                    </select>
                                                    {errors.status && touched.status && <div className="text-danger">{errors.status}</div>}
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <div className="form-group mb-3">
                                                        <label className="text-label">Vehicle Purchase Value</label>
                                                        <input
                                                            type="number"
                                                            name="vehicle_purchase_value"
                                                            className="form-control"
                                                            placeholder="Enter purchase value"
                                                            step="0.01"
                                                            value={values.vehicle_purchase_value}
                                                            onChange={handleChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-lg-6 mb-2">
                                                    <label className="text-label">Branch</label>
                                                    <Select
                                                        className="basic-single"
                                                        classNamePrefix="select"
                                                        options={branches.map((branch) => ({
                                                            value: branch.id,
                                                            label: branch.name,
                                                            data: branch,
                                                        }))}
                                                        value={
                                                            values.branchId
                                                                ? branches
                                                                    .map((branch) => ({
                                                                        value: branch.id,
                                                                        label: branch.name,
                                                                        data: branch,
                                                                    }))
                                                                    .find((option) => String(option.value) === String(values.branchId)) || null
                                                                : null
                                                        }
                                                        onChange={(selectedOption) => handleBranchSelect(selectedOption, setFieldValue)}
                                                        isClearable
                                                        placeholder="Select Branch"
                                                    />
                                                </div>

                                                <div className="col-lg-6 mb-2">
                                                    <label className="text-label">Department</label>
                                                    <Select
                                                        className="basic-single"
                                                        classNamePrefix="select"
                                                        options={filteredDepartments.map((department) => ({
                                                            value: department.id,
                                                            label: department.name,
                                                            data: department,
                                                        }))}
                                                        value={
                                                            values.departmentId
                                                                ? filteredDepartments
                                                                    .map((department) => ({
                                                                        value: department.id,
                                                                        label: department.name,
                                                                        data: department,
                                                                    }))
                                                                    .find((option) => String(option.value) === String(values.departmentId)) || null
                                                                : null
                                                        }
                                                        onChange={(selectedOption) => handleDepartmentSelect(selectedOption, setFieldValue, values.branchId)}
                                                        isClearable
                                                        placeholder="Select Department"
                                                        isDisabled={!values.branchId && filteredDepartments.length === 0}
                                                    />
                                                </div>

                                                <div className="col-lg-6 mb-2">
                                                    <label className="text-label">Sub Department</label>
                                                    <Select
                                                        className="basic-single"
                                                        classNamePrefix="select"
                                                        options={filteredSubDepartments.map((subDepartment) => ({
                                                            value: subDepartment.id,
                                                            label: subDepartment.name,
                                                            data: subDepartment,
                                                        }))}
                                                        value={
                                                            values.subDepartmentId
                                                                ? filteredSubDepartments
                                                                    .map((subDepartment) => ({
                                                                        value: subDepartment.id,
                                                                        label: subDepartment.name,
                                                                        data: subDepartment,
                                                                    }))
                                                                    .find((option) => String(option.value) === String(values.subDepartmentId)) || null
                                                                : null
                                                        }
                                                        onChange={(selectedOption) => handleSubDepartmentSelect(selectedOption, setFieldValue)}
                                                        isClearable
                                                        placeholder="Select Sub Department"
                                                        isDisabled={!values.departmentId && filteredSubDepartments.length === 0}
                                                    />
                                                </div>
                                                <div className="toolbar toolbar-bottom p-2">
                                                    <div className="d-flex flex-row justify-content-end gap-2">
                                                        <button
                                                            type="button"
                                                            className="btn btn-cancel"
                                                            onClick={navigateBackSafely}
                                                            disabled={isSubmitting}
                                                            style={{ flex: '1', maxWidth: '200px', minWidth: '100px' }}
                                                        >
                                                            Cancel
                                                        </button>
                                                        <button
                                                            type="submit"
                                                            className="btn user-form-btn sw-btn-next"
                                                            disabled={isSubmitting}
                                                            style={{ flex: '1', maxWidth: '200px', minWidth: '100px' }}
                                                        >
                                                            {loading ? (
                                                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                                                            ) : null}
                                                            Submit
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                            </section>
                        </div>
                    </div>
                </div>
                <InternalFooter />
            </div>
        </div>
    );
};

export default AddVehicle;